// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, DevicePhoneMobileIcon, ComputerDesktopIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import routes_web from 'connections/routes_web';

// Opções de instalação (espelham backend pwa_config; default + notepad têm ícones próprios)
const PWA_INSTALL_OPTIONS = [
  { value: 'main', label: 'Quinas (geral)', name: 'Quinas Ecosystem' },
  { value: 'planner', label: 'Planner', name: 'Quinas Planner' },
  { value: 'finance', label: 'Finance', name: 'Quinas Finance' },
  { value: 'notepad', label: 'Notepad', name: 'Quinas Notepad' },
  { value: 'health', label: 'Health', name: 'Quinas Health' },
  { value: 'travel', label: 'Travel', name: 'Quinas Travel' },
  { value: 'core', label: 'Core', name: 'Quinas Core' },
];

// ###### MODAL INSTALL APP COMPONENT
const ModalInstallApp = ({ isOpen, onClose, onInstall, isInstalled, deferredPrompt, initialModule = 'main' }) => {
  const [selectedModule, setSelectedModule] = useState(initialModule);

  useEffect(() => {
    if (isOpen && initialModule) {
      setSelectedModule(initialModule);
    }
  }, [isOpen, initialModule]);

  const selectedOption = PWA_INSTALL_OPTIONS.find((o) => o.value === selectedModule) || PWA_INSTALL_OPTIONS[0];

  const handleModuleChange = (e) => {
    const newValue = e.target.value;
    setSelectedModule(newValue);
    const params = new URLSearchParams(window.location.search);
    params.set('install', '1');
    params.set('module', newValue);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.location.href = newUrl;
  };

  // ##### DETECT DEVICE AND SET INITIAL TAB
  const getInitialTab = () => {
    // Priority 1: Check if instant download is available
    if (deferredPrompt && !isInstalled) {
      return 'instant';
    }
    
    // Priority 2: Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      return 'ios';
    }
    
    // Priority 3: Default to Android
    return 'android';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());

  // ##### RESET TAB WHEN MODAL OPENS OR CONDITIONS CHANGE
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(getInitialTab());
    }
  }, [isOpen, deferredPrompt, isInstalled]);

  // ##### TAB CONFIGURATION
  const tabs = [
    {
      id: 'instant',
      name: 'Instant Download',
      icon: ArrowDownTrayIcon,
      condition: deferredPrompt && !isInstalled
    },
    {
      id: 'ios',
      name: 'Using iOS?',
      icon: DevicePhoneMobileIcon,
      condition: true
    },
    {
      id: 'android',
      name: 'Using Android/Chrome?',
      icon: DevicePhoneMobileIcon,
      condition: true
    },
    {
      id: 'other',
      name: 'Other devices',
      icon: ComputerDesktopIcon,
      condition: true
    }
  ];

  // ##### RENDER TAB CONTENT
  const renderTabContent = () => {
    switch (activeTab) {
      case 'instant':
        return (
          <div className="space-y-4">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <ArrowDownTrayIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Ready to Install!</h3>
              <p className="text-gray-300 text-sm">
                Your device supports direct app installation. Click the button below to add {selectedOption.name} to your device home screen.
              </p>
            </div>
            <button
              onClick={onInstall}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Install App Now
            </button>
          </div>
        );

      case 'ios':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <DevicePhoneMobileIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-4">iOS Installation</h3>
            </div>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                <div className="flex-1">
                  <p className="text-gray-200 text-sm font-medium">Tap the Share button</p>
                  <p className="text-gray-400 text-xs mb-2">Located at the bottom of your Safari browser</p>
                  <img src={routes_web.img_install_ios_step1} className="w-full max-w-64 rounded border border-gray-600" alt="iOS Step 1" />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                <div className="flex-1">
                  <p className="text-gray-200 text-sm font-medium">Select "Add to Home Screen"</p>
                  <p className="text-gray-400 text-xs mb-2">Scroll down in the share menu to find this option</p>
                  <img src={routes_web.img_install_ios_step2} className="w-full max-w-64 rounded border border-gray-600" alt="iOS Step 2" />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                <div className="flex-1">
                  <p className="text-gray-200 text-sm font-medium">Tap "Add"</p>
                  <p className="text-gray-400 text-xs">Confirm to add the app to your home screen</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'android':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <DevicePhoneMobileIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-4">Android/Chrome Installation</h3>
            </div>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                <div className="flex-1">
                  <p className="text-gray-200 text-sm font-medium">Look for the browser options</p>
                  <p className="text-gray-400 text-xs mb-2">The 'three dots' menu is usually located at the top right</p>
                  <img src={routes_web.img_install_android_step1} className="w-full max-w-64 rounded border border-gray-600" alt="Android Step 1" />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                <div className="flex-1">
                  <p className="text-gray-200 text-sm font-medium">Tap "Add to Home screen"</p>
                  <p className="text-gray-400 text-xs mb-2">Scroll down in the menu to find this option</p>
                  <img src={routes_web.img_install_android_step2} className="w-full max-w-64 rounded border border-gray-600" alt="Android Step 2" />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                <div className="flex-1">
                  <p className="text-gray-200 text-sm font-medium">Confirm installation</p>
                  <p className="text-gray-400 text-xs">Tap "Add" to install the app on your home screen</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'other':
        return (
          <div className="space-y-3">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <ComputerDesktopIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Other Devices</h3>
            </div>
            <div className="space-y-3">
              <div className="bg-gray-700 rounded-lg p-3">
                <h4 className="text-gray-200 font-medium mb-1">Desktop/Laptop</h4>
                <p className="text-gray-400 text-sm">
                  Use your browser's bookmark feature to save this page for easy access.
                </p>
              </div>
              <div className="bg-gray-700 rounded-lg p-3">
                <h4 className="text-gray-200 font-medium mb-1">Other Mobile</h4>
                <p className="text-gray-400 text-sm">
                  Try using Chrome or Edge browser for better experience, or bookmark this page.
                </p>
              </div>
              <div className="bg-gray-700 rounded-lg p-3">
                <h4 className="text-gray-200 font-medium mb-1">Alternative Access</h4>
                <p className="text-gray-400 text-sm">
                  You can always access Quinas Ecosystem through your browser's address bar.
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ##### RETURN
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md relative h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-2 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-gray-100">Install {selectedOption.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Dropdown: qual app instalar */}
        <div className="px-3 py-2 border-b border-gray-700">
          <label className="block text-xs font-medium text-gray-400 mb-1">Install as</label>
          <div className="relative">
            <select
              value={selectedModule}
              onChange={handleModuleChange}
              className="w-full appearance-none bg-gray-700 text-gray-100 border border-gray-600 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {PWA_INSTALL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          {tabs.filter(tab => tab.condition).map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full px-2 py-3 text-xs font-medium transition-colors h-16 flex flex-col items-center justify-center ${
                  activeTab === tab.id
                    ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-750'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <span className="text-center leading-tight break-words">{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-4 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 hover:scrollbar-thumb-gray-500">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ModalInstallApp;
